import React from "react";
import Grid from '@mui/material/Grid';
import {
  Box,
  Container,
  Typography,
  Card,
  Avatar,
  Button,
  useTheme,
  useMediaQuery,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import {
  Rocket as RocketIcon,
  CheckCircle as CheckCircleIcon,
} from "@mui/icons-material";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

export default function About() {
  const { t } = useTranslation();
  const aboutT = (key: string) => t(`about:${key}`);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const why = [
    aboutT("why1"),
    aboutT("why2"),
    aboutT("why3"),
    aboutT("why4"),
    aboutT("why5")
  ];

  const team = [
    {
      name: aboutT("teamMember1Name"),
      role: aboutT("teamMember1Role"),
      avatar: "/images/team/sophia.jpg",
      description: aboutT("teamMember1Description"),
    },
    {
      name: aboutT("teamMember2Name"),
      role: aboutT("teamMember2Role"),
      avatar: "/images/team/alice.jpg",
      description: aboutT("teamMember2Description"),
    },
    {
      name: aboutT("teamMember3Name"),
      role: aboutT("teamMember3Role"),
      avatar: "/images/team/michael.jpg",
      description: aboutT("teamMember3Description"),
    },
    {
      name: aboutT("teamMember4Name"),
      role: aboutT("teamMember4Role"),
      avatar: "/images/team/sophia.jpg",
      description: aboutT("teamMember4Description"),
    },
    {
      name: aboutT("teamMember5Name"),
      role: aboutT("teamMember5Role"),
      avatar: "/images/team/sophia.jpg",
      description: aboutT("teamMember5Description"),
    },
    {
      name: aboutT("teamMember6Name"),
      role: aboutT("teamMember6Role"),
      avatar: "/images/team/sophia.jpg",
      description: aboutT("teamMember6Description"),
    }
  ];

  return (
    <Box sx={{ overflow: "hidden" }}>
      {/* Hero Section */}
      <Box
        sx={{
          background: `
            linear-gradient(90deg, #f8fafc 0%, transparent 25%),
            linear-gradient(270deg, #f8fafc 0%, transparent 25%),
            linear-gradient(180deg, cadetblue 0%, transparent 90%)
          `,
          py: { xs: 2, md: 5 },
          textAlign: "center",
        }}
      >
        <Container maxWidth="lg">
          <Typography variant="h2" sx={{ pb: { xs: 2 } }}>
            {aboutT("aboutHeroTitle")}
          </Typography>
          <Typography variant="body2" textAlign={"justify"}>
            {aboutT("aboutHeroDescription")}
          </Typography>
        </Container>
      </Box>

      {/* Mission & Vision Section */}
      <Container sx={{ py: { xs: 1, md: 4 } }}>
        <Grid container spacing={4}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Card
              sx={{
                textAlign: "center",
                p: { xs: 2, md: 4 },
                height: "100%",
                borderRadius: 2,
                boxShadow: 3
              }}
            >
              <Typography variant="h4">
                {aboutT("missionTitle")}
              </Typography>
              <Typography variant="body2" sx={{ lineHeight: 1.8, textAlign: 'center', pl: 1 }}>
                {aboutT("missionDescription")}
              </Typography>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Card
              sx={{
                textAlign: "center",
                p: { xs: 2, md: 4 },
                height: "100%",
                borderRadius: 2,
                boxShadow: 3,
              }}
            >
              <Typography variant="h4">
                {aboutT("visionTitle")}
              </Typography>
              <Typography variant="body2" sx={{ lineHeight: 1.8, textAlign: 'center', pl: 1 }}>
                {aboutT("visionDescription")}
              </Typography>
            </Card>
          </Grid>
        </Grid>
      </Container>

      {/* Team Section */}
      <Box sx={{ backgroundColor: "transparent", py: { xs: 2, md: 8 } }}>
        <Container maxWidth="lg">
          <Box sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: "center",
            gap: 6
          }}>
            {/* Left side: Text */}
            <Box sx={{ backgroundColor: "transparent", py: { xs: 4, md: 8 } }}>
              <Typography variant="h3">
                {aboutT("differentTitle")}
              </Typography>
              <List dense>
                {why.map((benefit, index) => (
                  <ListItem key={index} sx={{ px: 0, py: 1 }}>
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <CheckCircleIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText primary={benefit} />
                  </ListItem>
                ))}
              </List>
            </Box>

            {/* Right side: Image */}
            <Box sx={{
              flex: 1,
              display: { xs: "none", md: "block" }
            }}>
              <img
                src="/logos/whyus.png"
                alt={aboutT("teamImageAlt")}
                style={{
                  width: "100%",
                  maxWidth: "600px",
                  height: "auto",
                  borderRadius: "12px",
                  background: "linear-gradient(90deg, transparent 0%, cadetblue 100%)"
                }}
              />
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Call to Action */}
      <Container sx={{ py: { xs: 1, md: 8 }, textAlign: "center" }}>
        <Typography variant="h4" sx={{ pb: 2 }}>
          {aboutT("ctaTitle")}
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mb: 4, maxWidth: "600px", mx: "auto", lineHeight: 1.8 }}
        >
          {aboutT("ctaDescription")}
        </Typography>
        <Button
          variant="contained"
          size="large"
          component={Link}
          to="/contact"
          startIcon={<RocketIcon />}
          sx={{
            px: 4,
            py: 1.5,
            background: "linear-gradient(135deg, transparent 0%, #1e40af 100%)"
          }}
        >
          {aboutT("ctaButton")}
        </Button>
      </Container>
    </Box>
  );
}